import {
  Divider,
  Menu,
  Text,
  useMenu,
  vars,
} from "@janeapp/burrito-design-system"
import { fullName, initials } from "core"
import { useContext } from "react"
import { useTranslation } from "react-i18next"
import styled, { css } from "styled-components"
import { AccountContext } from "../../context/AccountContext"
import { ReactComponent as JaneLogo } from "../../images/JaneLogo.svg"

// A native button, because useMenu types triggerProps.ref as
// Ref<HTMLButtonElement> while Burrito's UnstyledButton renders a div.
const Avatar = styled.button(
  () => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${vars.global.size["10x"]};
    height: ${vars.global.size["10x"]};
    border: ${vars.alias.border.width.sm} solid
      ${vars.alias.border.color.default};
    border-radius: 50%;
    cursor: pointer;
    flex-shrink: 0;
    background: ${vars.alias.background.color.emphasis.default};
    color: ${vars.alias.text.color.default};
    font-family: ${vars.global.typography.fontFamily};
    font-size: ${vars.global.typography.fontSize.lg};
    line-height: ${vars.global.typography.lineHeight.md};
    letter-spacing: ${vars.global.typography.letterSpacing.lg};
    font-weight: ${vars.global.typography.weight.semibold};
  `
)

// Both header lines are 16px semibold; only the second is subdued.
const HeaderText = styled(Text)(
  () => css`
    line-height: ${vars.global.typography.lineHeight.md};
    font-weight: ${vars.global.typography.weight.semibold};
  `
)

const StyledMenu = styled(Menu)(
  () => css`
    min-width: 190px;
    width: max-content;
    padding: 0;

    /* Reset Burrito's default menu/group/header padding so our container controls spacing */
    & > div {
      padding: 0;
    }

    & > div > div:first-child {
      padding: 0;
    }

    [role="group"] {
      padding: 0;
    }

    [role="menuitem"] {
      min-height: auto;
      padding: ${vars.global.size["3x"]} ${vars.global.size["4x"]}
        ${vars.global.size["4x"]};
      font-size: ${vars.global.typography.fontSize.md};
      line-height: ${vars.global.typography.lineHeight.md};
      letter-spacing: ${vars.global.typography.letterSpacing.md};
      font-weight: ${vars.global.typography.weight.regular};
      color: ${vars.alias.text.color.critical};
      border-radius: 0 0 ${vars.alias.border.radius.md}
        ${vars.alias.border.radius.md};

      &:hover {
        background: transparent;
        color: ${vars.alias.text.color.critical};
        text-decoration: underline;
      }
    }
  `
)

const Identity = styled.div(
  () => css`
    display: flex;
    flex-direction: column;
    padding: ${vars.global.size["4x"]} ${vars.global.size["4x"]} 0;
  `
)

const IdentityName = styled(Text)(
  () => css`
    color: ${vars.alias.text.color.default};
    font-size: ${vars.global.typography.fontSize.lg};
    line-height: ${vars.global.typography.lineHeight.md};
    letter-spacing: ${vars.global.typography.letterSpacing.lg};
    font-weight: ${vars.global.typography.weight.semibold};
  `
)

const PopoverDivider = styled(Divider)(
  () => css`
    margin-top: ${vars.global.size["3x"]};
    margin-bottom: 0;
    margin-left: -${vars.global.size["4x"]};
    margin-right: -${vars.global.size["4x"]};
  `
)

export const AccountHeader = ({ logout }: { logout: () => void }) => {
  const { t } = useTranslation()
  const { profile } = useContext(AccountContext)
  const { triggerProps, menuProps } = useMenu()

  // Menu.Group renders its title through useMenuSection, so the identity block
  // and its rule live in the title. A lone group gets no border of its own.
  const identityBlock = (
    <Identity>
      <IdentityName elementType="span">
        {profile ? fullName(profile) : ""}
      </IdentityName>
      <Text elementType="span" size="md" color="subdued">
        {profile?.email}
      </Text>
      <PopoverDivider />
    </Identity>
  )

  return (
    <header className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {/* Renders at its intrinsic 54x44, already in brand teal. */}
        <JaneLogo className="shrink-0" />
        <div className="flex flex-col">
          <HeaderText elementType="span" size="lg">
            {t("navigation.janeId")}
          </HeaderText>
          <HeaderText elementType="span" size="lg" color="subdued">
            {t("navigation.accountDetails")}
          </HeaderText>
        </div>
      </div>

      <Avatar {...triggerProps} aria-label={t("navigation.accountMenu")}>
        {profile ? initials(profile) : ""}
      </Avatar>

      <StyledMenu
        {...menuProps}
        position="right"
        aria-label={t("navigation.accountMenu")}
        onAction={() => logout()}
      >
        <Menu.Group title={identityBlock}>
          <Menu.Item key="sign-out" isCritical>
            {t("navigation.logout")}
          </Menu.Item>
        </Menu.Group>
      </StyledMenu>
    </header>
  )
}
